import React, { useEffect, useState } from 'react';
import { X, ExternalLink, FileText, Image as ImageIcon } from 'lucide-react';
import { AdminMenuImportDetail, AdminMenuImportRow } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import {
  formatDateTimeWib,
  formatDuration,
  formatNumber,
  formatRupiah,
  formatUsd,
  menuImportResultBadge,
} from '../utils/menuImport';

interface Props {
  row: AdminMenuImportRow | null;
  isOpen: boolean;
  onClose: () => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500">{label}</dt>
    <dd className="mt-0.5 text-sm text-gray-900 break-words">{children}</dd>
  </div>
);

const MenuImportDetailSlideOver: React.FC<Props> = ({ row, isOpen, onClose }) => {
  const [detail, setDetail] = useState<AdminMenuImportDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !row?.id) return;
    let cancelled = false;
    setDetail(null);
    setError(null);
    setLoading(true);
    api
      .getAdminMenuImport(row.id)
      .then((data) => { if (!cancelled) setDetail(data ?? null); })
      .catch((err: any) => { if (!cancelled) setError(err?.response?.data?.message ?? 'Gagal memuat detail import.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, row?.id]);

  if (!isOpen || !row) return null;

  // Tampilkan data baris dulu selama detail dimuat.
  const data = detail ?? row;
  const tokens = data.tokens;
  const cost = data.cost;
  const commit = data.commitSummary;
  const files = detail?.files ?? [];

  return (
    <div className="fixed inset-0 overflow-hidden z-50">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
          <div className="w-screen max-w-lg">
            <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
              <div className="px-4 py-6 bg-gray-50 sm:px-6 border-b border-gray-200">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Detail Import Foto Menu</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {data.company?.name ?? '—'} · {formatDateTimeWib(data.createdAt)}
                    </p>
                  </div>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Tutup">
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="flex-1 px-4 py-6 sm:px-6 space-y-6">
                {loading && <LoadingSpinner size="sm" text="Memuat detail..." />}
                {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Ringkasan</h3>
                  <dl className="grid grid-cols-2 gap-4">
                    <Field label="Hasil">{menuImportResultBadge(data.result, data.resultLabel)}</Field>
                    <Field label="Status">{data.status ?? '—'}</Field>
                    <Field label="Brand">
                      {data.company?.name ?? '—'}
                      <div className="text-xs text-gray-400 font-mono">{data.company?.id ?? ''}</div>
                    </Field>
                    <Field label="User">{data.user?.name ?? '—'}</Field>
                    <Field label="Model">{data.model ?? '—'}</Field>
                    <Field label="Unit (foto/halaman)">
                      {formatNumber(data.units)}
                      {data.pdfTrimmed ? <span className="ml-1 text-xs text-yellow-700">(PDF dipotong)</span> : null}
                    </Field>
                    <Field label="Durasi">{formatDuration(data.durationSeconds)}</Field>
                    <Field label="Item terbaca">{formatNumber(data.itemsRead)}</Field>
                    <Field label="Kuota dipotong">{formatDateTimeWib(data.chargedAt)}</Field>
                    <Field label="Disimpan">{formatDateTimeWib(data.committedAt)}</Field>
                  </dl>
                </section>

                {(data.errorCode || data.errorMessage) && (
                  <section className="rounded-md bg-red-50 p-3">
                    <h3 className="text-sm font-semibold text-red-800">Error {data.errorCode ? `· ${data.errorCode}` : ''}</h3>
                    <p className="mt-1 text-sm text-red-700">{data.errorMessage ?? '—'}</p>
                  </section>
                )}

                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Token & Biaya</h3>
                  <table className="min-w-full text-sm">
                    <tbody className="divide-y divide-gray-100">
                      <tr><td className="py-1.5 text-gray-500">Input</td><td className="py-1.5 text-right">{formatNumber(tokens?.input)}</td></tr>
                      <tr><td className="py-1.5 text-gray-500">Output</td><td className="py-1.5 text-right">{formatNumber(tokens?.output)}</td></tr>
                      <tr><td className="py-1.5 text-gray-500">Cache read (×0,10)</td><td className="py-1.5 text-right">{formatNumber(tokens?.cacheRead)}</td></tr>
                      <tr><td className="py-1.5 text-gray-500">Cache write (×1,25)</td><td className="py-1.5 text-right">{formatNumber(tokens?.cacheWrite)}</td></tr>
                      <tr className="font-medium"><td className="py-1.5">Total token</td><td className="py-1.5 text-right">{formatNumber(tokens?.total)}</td></tr>
                      <tr className="font-medium">
                        <td className="py-1.5">Biaya</td>
                        <td className="py-1.5 text-right">
                          {cost?.priced === false ? (
                            <span className="text-yellow-700">Model belum ada harga</span>
                          ) : (
                            <>
                              {formatRupiah(cost?.idr)}
                              <div className="text-xs text-gray-500 font-normal">{formatUsd(cost?.usd)}</div>
                            </>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </section>

                {detail?.resultSummary && (
                  <section>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Hasil Baca AI</h3>
                    <dl className="grid grid-cols-2 gap-4">
                      <Field label="Item">{formatNumber(detail.resultSummary.items)}</Field>
                      <Field label="Saran template opsi">{formatNumber(detail.resultSummary.templates)}</Field>
                    </dl>
                    {(detail.resultSummary.categories ?? []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {(detail.resultSummary.categories ?? []).map((category) => (
                          <span key={category} className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-xs text-gray-700">{category}</span>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {commit && (
                  <section>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Disimpan ke Produk</h3>
                    <dl className="grid grid-cols-2 gap-4">
                      <Field label="Produk baru">{formatNumber(commit.created)}</Field>
                      <Field label="Produk diperbarui">{formatNumber(commit.updated)}</Field>
                      <Field label="Template baru">{formatNumber(commit.templatesCreated)}</Field>
                      <Field label="Kategori baru">{formatNumber(commit.categoriesCreated)}</Field>
                    </dl>
                  </section>
                )}

                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">File ({(detail?.files ?? row.files ?? []).length})</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    File mentah dihapus setelah AI selesai membaca, jadi biasanya sudah tidak tersedia. Link berlaku ±10 menit.
                  </p>
                  <ul className="space-y-2">
                    {(detail ? files : (row.files ?? []).map((f, index) => ({ ...f, index, available: false, url: null }))).map((file) => {
                      const isImage = (file.mime ?? '').startsWith('image/');
                      return (
                        <li key={`${file.index}-${file.name}`} className="flex items-center gap-3 rounded-md border border-gray-200 p-2">
                          {file.available && file.url && isImage ? (
                            <a href={file.url} target="_blank" rel="noopener noreferrer">
                              <img src={file.url} alt={file.name} className="h-16 w-16 rounded object-cover bg-gray-100" />
                            </a>
                          ) : (
                            <div className="h-16 w-16 rounded bg-gray-100 flex items-center justify-center text-gray-400">
                              {isImage ? <ImageIcon className="h-6 w-6" /> : <FileText className="h-6 w-6" />}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{file.name || '—'}</div>
                            <div className="text-xs text-gray-500">{file.mime ?? '—'}</div>
                          </div>
                          {file.available && file.url ? (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
                            >
                              Buka <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">{detail ? 'Sudah dihapus' : ''}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>

                <p className="text-xs text-gray-400 font-mono break-all">ID: {data.id}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuImportDetailSlideOver;
